import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { 
  Home, 
  ArrowLeft, 
  ArrowRight,
  Phone, 
  Mail, 
  Calendar,
  CheckCircle,
  Maximize2,
  Bed,
  Bath,
  Car,
  TreePine,
  Zap,
  Droplets,
  ChevronRight,
  Share2,
  Heart,
  Users,
  Ruler,
  Palette,
  Wrench,
  FileText,
  Lightbulb
} from 'lucide-react';
import { Button } from '../components/ui/button';
import AnimatedSection from '../components/AnimatedSection';
import { houseModels } from '../mockData';

const ModelDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [model, setModel] = useState(null);
  const [isFavorite, setIsFavorite] = useState(false);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [activeTab, setActiveTab] = useState('overview');
  const [isGalleryHovered, setIsGalleryHovered] = useState(false);

  // Генерируем расширенные данные для модели
  const getModelDetails = (model) => {
    const isPlainPied = model.type === 'Plain-pied';
    const isEtage = model.type === 'Étage';
    const isDemiNiveau = model.type === 'Demi-niveau';
    
    // Базовые характеристики в зависимости от типа
    const baseSurface = isPlainPied ? 120 : isEtage ? 140 : 130;
    const baseRooms = isPlainPied ? 4 : isEtage ? 5 : 4;
    const baseBedrooms = isPlainPied ? 3 : isEtage ? 4 : 3;
    const baseBathrooms = isPlainPied ? 2 : isEtage ? 2 : 2;
    
    // Собираем longDescription по частям
    let longDescriptionText = `Découvrez la ${model.title}, un modèle de maison ${model.style.toLowerCase()} qui allie modernité et tradition. Cette maison ${model.type.toLowerCase()} est conçue pour offrir un maximum de confort et d'espace. L'architecture ${model.style.toLowerCase()} apporte une touche d'élégance et de caractère à votre projet de construction.`;
    
    if (isPlainPied) {
      longDescriptionText += ' Le plain-pied offre une accessibilité optimale et une circulation fluide entre les espaces.';
    }
    if (isEtage) {
      longDescriptionText += ' La maison à étage permet d\'optimiser l\'emprise au sol tout en offrant de beaux volumes.';
    }
    if (isDemiNiveau) {
      longDescriptionText += ' Le demi-niveau apporte une séparation douce des espaces tout en conservant une sensation d\'ouverture.';
    }
    
    return {
      description: `La ${model.title} est une maison ${model.style.toLowerCase()} ${model.type.toLowerCase()}, conçue pour allier confort, esthétique et fonctionnalité. Ce modèle ${model.name} s'adapte parfaitement à votre mode de vie et à vos besoins. Avec son architecture ${model.style.toLowerCase()}, cette maison offre un espace de vie optimisé et personnalisable selon vos préférences.`,
      
      longDescription: longDescriptionText,
      
      characteristics: {
        surface: baseSurface,
        livingArea: Math.round(baseSurface * 0.7),
        rooms: baseRooms,
        bedrooms: baseBedrooms,
        bathrooms: baseBathrooms,
        parking: 2,
        floors: isPlainPied ? 1 : isEtage ? 2 : 1.5,
        orientation: 'Sud/Sud-Ouest',
        energyClass: 'A',
        constructionTime: '8-12 mois',
        warranty: '10 ans'
      },
      
      features: [
        { icon: Maximize2, label: 'Surface habitable', value: `${baseSurface} m²` },
        { icon: Home, label: 'Pièces', value: `${baseRooms}` },
        { icon: Bed, label: 'Chambres', value: `${baseBedrooms}` },
        { icon: Bath, label: 'Salles de bain', value: `${baseBathrooms}` },
        { icon: Car, label: 'Places de parking', value: '2' },
        { icon: Zap, label: 'Classe énergétique', value: 'A' }
      ],
      
      technicalSpecs: [
        { category: 'Structure', items: ['Béton armé', 'Isolation renforcée', 'Charpente bois'] },
        { category: 'Énergie', items: ['Pompe à chaleur', 'VMC double flux', 'Panneaux solaires (option)'] },
        { category: 'Finitions', items: ['Fenêtres double vitrage', 'Portes intérieures massives', 'Carrelage et parquet'] },
        { category: 'Sécurité', items: ['Alarme', 'Volets roulants électriques', 'Porte blindée'] }
      ],
      
      personalization: [
        'Agencement intérieur modifiable',
        'Choix des matériaux et finitions',
        'Extension possible',
        'Aménagement extérieur (terrasse, pergola)',
        'Options domotique',
        'Personnalisation des couleurs'
      ],
      
      advantages: [
        'Architecture adaptée au terrain',
        'Conception bioclimatique',
        'Isolation performante',
        'Équipements modernes inclus',
        'Accompagnement personnalisé',
        'Garantie décennale'
      ],
      
      gallery: [
        model.image,
        'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=800',
        'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=800',
        'https://images.unsplash.com/photo-1600607687644-c7171b42498b?w=800',
        'https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?w=800'
      ],
      
      floorPlans: [
        { name: 'Rez-de-chaussée', image: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=800' },
        ...(isEtage ? [{ name: 'Étage', image: 'https://images.unsplash.com/photo-1600607687644-c7171b42498b?w=800' }] : [])
      ]
    };
  };

  useEffect(() => {
    const foundModel = houseModels.find(m => m.id === id);
    if (foundModel) {
      setModel(foundModel);
    }
  }, [id]);

  useEffect(() => {
    if (!model) {
      return;
    }

    const gallery = getModelDetails(model).gallery;

    if (!gallery || gallery.length <= 1 || isGalleryHovered) {
      return;
    }

    const interval = setInterval(() => {
      setActiveImageIndex((prevIndex) => {
        const total = gallery.length;
        return total > 0 ? (prevIndex + 1) % total : 0;
      });
    }, 4000);

    return () => clearInterval(interval);
  }, [model, isGalleryHovered]);

  if (!model) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-gray-800 mb-4">Modèle introuvable</h1>
          <Button onClick={() => navigate('/modeles')} className="bg-[#c4d600] hover:bg-[#b0c200] text-gray-800">
            Retour aux modèles
          </Button>
        </div>
      </div>
    );
  }

  const details = getModelDetails(model);
  
  // Находим похожие модели
  const similarModels = houseModels
    .filter(m => m.id !== model.id && (m.type === model.type || m.style === model.style))
    .slice(0, 3);

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: model.title,
          text: `Découvrez ce modèle de maison ${model.name}`,
          url: window.location.href
        });
      } catch (err) {
        console.log('Error sharing:', err);
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('Lien copié dans le presse-papiers !');
    }
  };

  const handlePrevImage = () => {
    if (!details.gallery || details.gallery.length === 0) return;
    setActiveImageIndex((prevIndex) => {
      const total = details.gallery.length;
      if (total === 0) return 0;
      return (prevIndex - 1 + total) % total;
    });
  };

  const handleNextImage = () => {
    if (!details.gallery || details.gallery.length === 0) return;
    setActiveImageIndex((prevIndex) => {
      const total = details.gallery.length;
      if (total === 0) return 0;
      return (prevIndex + 1) % total;
    });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Breadcrumb */}
      <AnimatedSection animation="fade-in">
        <div className="bg-gray-50 py-4">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center text-sm text-gray-600">
              <Link to="/" className="hover:text-[#c4d600] transition-colors">Accueil</Link>
              <ChevronRight className="w-4 h-4 mx-2" />
              <Link to="/modeles" className="hover:text-[#c4d600] transition-colors">Nos modèles</Link>
              <ChevronRight className="w-4 h-4 mx-2" />
              <span className="text-gray-800 font-medium">{model.title}</span>
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* Hero Section with Image Gallery */}
      <AnimatedSection animation="fade-up">
        <div className="relative">
          <div
            className="relative h-[60vh] md:h-[70vh] overflow-hidden bg-gray-200"
            onMouseEnter={() => setIsGalleryHovered(true)}
            onMouseLeave={() => setIsGalleryHovered(false)}
          >
            <img
              src={details.gallery[activeImageIndex]}
              alt={model.title}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.src = 'https://via.placeholder.com/1200x800?text=Image+non+disponible';
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
            
            {/* Image Navigation */}
            {details.gallery.length > 1 && (
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2 z-10">
                {details.gallery.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setActiveImageIndex(index)}
                    className={`w-3 h-3 rounded-full transition-all ${
                      activeImageIndex === index ? 'bg-[#c4d600] w-8' : 'bg-white/50 hover:bg-white/80'
                    }`}
                    aria-label={`Voir l'image ${index + 1}`}
                  />
                ))}
              </div>
            )}

            {/* Arrow Navigation */}
            {details.gallery.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={handlePrevImage}
                  className="absolute left-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/20 backdrop-blur-md text-white hover:bg-white/30 transition-all z-10"
                  aria-label="Image précédente"
                >
                  <ArrowLeft className="w-5 h-5" />
                </button>
                <button
                  type="button"
                  onClick={handleNextImage}
                  className="absolute right-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/20 backdrop-blur-md text-white hover:bg-white/30 transition-all z-10"
                  aria-label="Image suivante"
                >
                  <ArrowRight className="w-5 h-5" />
                </button>
              </>
            )}

            {/* Action Buttons */}
            <div className="absolute top-4 right-4 flex space-x-2 z-10">
              <button
                onClick={() => setIsFavorite(!isFavorite)}
                className={`p-3 rounded-full backdrop-blur-md transition-all ${
                  isFavorite 
                    ? 'bg-[#c4d600] text-gray-800' 
                    : 'bg-white/20 text-white hover:bg-white/30'
                }`}
                aria-label={isFavorite ? "Retirer des favoris" : "Ajouter aux favoris"}
              >
                <Heart className={`w-5 h-5 ${isFavorite ? 'fill-current' : ''}`} />
              </button>
              <button
                onClick={handleShare}
                className="p-3 rounded-full bg-white/20 backdrop-blur-md text-white hover:bg-white/30 transition-all"
                aria-label="Partager"
              >
                <Share2 className="w-5 h-5" />
              </button>
            </div>

            {/* Back Button */}
            <div className="absolute top-4 left-4 z-10">
              <Button
                onClick={() => navigate('/modeles')}
                variant="ghost"
                className="bg-white/20 backdrop-blur-md text-white hover:bg-white/30"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Retour
              </Button>
            </div>

            {/* Title Overlay */}
            <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
              <div className="max-w-7xl mx-auto">
                <div className="flex items-center gap-3 mb-2">
                  <span className="px-3 py-1 bg-[#c4d600] text-gray-800 rounded-full text-sm font-semibold">
                    Modèle {model.name}
                  </span>
                  <span className="px-3 py-1 bg-white/20 backdrop-blur-md rounded-full text-sm">
                    {model.type}
                  </span>
                  <span className="px-3 py-1 bg-white/20 backdrop-blur-md rounded-full text-sm">
                    {model.style}
                  </span>
                </div>
                <h1 className="text-4xl md:text-5xl font-bold mb-4">{model.title}</h1>
                <div className="flex flex-wrap items-center gap-4 text-lg">
                  <div className="flex items-center">
                    <Maximize2 className="w-5 h-5 mr-2 text-[#c4d600]" />
                    <span>{details.characteristics.surface} m²</span>
                  </div>
                  <div className="flex items-center">
                    <Bed className="w-5 h-5 mr-2 text-[#c4d600]" />
                    <span>{details.characteristics.bedrooms} chambres</span>
                  </div>
                  <div className="flex items-center">
                    <Home className="w-5 h-5 mr-2 text-[#c4d600]" />
                    <span>{details.characteristics.rooms} pièces</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* Quick Info Bar */}
      <AnimatedSection animation="fade-up" delay={0.1}>
        <div className="bg-[#c4d600] py-6">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <div className="flex flex-wrap gap-6">
                {details.features.map((feature, index) => (
                  <div key={index} className="flex items-center text-gray-800">
                    <feature.icon className="w-5 h-5 mr-2" />
                    <div>
                      <p className="text-xs opacity-80">{feature.label}</p>
                      <p className="font-bold">{feature.value}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex gap-3">
                <Button
                  asChild
                  className="bg-gray-800 hover:bg-gray-900 text-white"
                >
                  <a href={`tel:0492000000`}>
                    <Phone className="w-4 h-4 mr-2" />
                    Appeler
                  </a>
                </Button>
                <Button
                  asChild
                  className="bg-gray-800 hover:bg-gray-900 text-white"
                >
                  <a href={`mailto:Contact@artduchalet.com?subject=Demande d'information - ${model.title}`}>
                    <Mail className="w-4 h-4 mr-2" />
                    Contacter
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* Tabs Navigation */}
      <AnimatedSection animation="fade-up" delay={0.2}>
        <div className="border-b border-gray-200 bg-white sticky top-20 z-40">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex space-x-8 overflow-x-auto">
              {[
                { id: 'overview', label: 'Vue d\'ensemble', icon: Home },
                { id: 'specs', label: 'Caractéristiques', icon: FileText },
                { id: 'plans', label: 'Plans', icon: Ruler },
                { id: 'personalization', label: 'Personnalisation', icon: Palette }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center py-4 border-b-2 transition-colors whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'border-[#c4d600] text-[#c4d600] font-semibold'
                      : 'border-transparent text-gray-600 hover:text-gray-800'
                  }`}
                >
                  <tab.icon className="w-4 h-4 mr-2" />
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </AnimatedSection>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <>
                <AnimatedSection animation="fade-up">
                  <div>
                    <h2 className="text-3xl font-bold text-gray-800 mb-4">Description</h2>
                    <p className="text-gray-600 text-lg leading-relaxed mb-4">{details.description}</p>
                    <p className="text-gray-600 text-lg leading-relaxed">{details.longDescription}</p>
                  </div>
                </AnimatedSection>

                <AnimatedSection animation="fade-up" delay={0.1}>
                  <div>
                    <h2 className="text-3xl font-bold text-gray-800 mb-6">Avantages</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {details.advantages.map((advantage, index) => (
                        <div
                          key={index}
                          className="flex items-start bg-gray-50 rounded-lg p-4 hover:bg-[#c4d600]/10 transition-colors"
                        >
                          <CheckCircle className="w-5 h-5 text-[#c4d600] mr-3 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-700">{advantage}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </AnimatedSection>
              </>
            )}

            {/* Specs Tab */}
            {activeTab === 'specs' && (
              <>
                <AnimatedSection animation="fade-up">
                  <div>
                    <h2 className="text-3xl font-bold text-gray-800 mb-6">Caractéristiques détaillées</h2>
                    <div className="bg-gray-50 rounded-lg p-6 mb-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="flex justify-between py-2 border-b border-gray-200">
                          <span className="text-gray-600">Surface habitable</span>
                          <span className="font-semibold text-gray-800">{details.characteristics.surface} m²</span>
                        </div>
                        <div className="flex justify-between py-2 border-b border-gray-200">
                          <span className="text-gray-600">Surface de séjour</span>
                          <span className="font-semibold text-gray-800">{details.characteristics.livingArea} m²</span>
                        </div>
                        <div className="flex justify-between py-2 border-b border-gray-200">
                          <span className="text-gray-600">Nombre de pièces</span>
                          <span className="font-semibold text-gray-800">{details.characteristics.rooms}</span>
                        </div>
                        <div className="flex justify-between py-2 border-b border-gray-200">
                          <span className="text-gray-600">Chambres</span>
                          <span className="font-semibold text-gray-800">{details.characteristics.bedrooms}</span>
                        </div>
                        <div className="flex justify-between py-2 border-b border-gray-200">
                          <span className="text-gray-600">Salles de bain</span>
                          <span className="font-semibold text-gray-800">{details.characteristics.bathrooms}</span>
                        </div>
                        <div className="flex justify-between py-2 border-b border-gray-200">
                          <span className="text-gray-600">Niveaux</span>
                          <span className="font-semibold text-gray-800">{details.characteristics.floors}</span>
                        </div>
                        <div className="flex justify-between py-2 border-b border-gray-200">
                          <span className="text-gray-600">Classe énergétique</span>
                          <span className="font-semibold text-gray-800">{details.characteristics.energyClass}</span>
                        </div>
                        <div className="flex justify-between py-2">
                          <span className="text-gray-600">Durée de construction</span>
                          <span className="font-semibold text-gray-800">{details.characteristics.constructionTime}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </AnimatedSection>

                <AnimatedSection animation="fade-up" delay={0.1}>
                  <div>
                    <h2 className="text-3xl font-bold text-gray-800 mb-6">Spécifications techniques</h2>
                    <div className="space-y-6">
                      {details.technicalSpecs.map((spec, index) => (
                        <div key={index} className="bg-gray-50 rounded-lg p-6">
                          <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                            {spec.category === 'Structure' && <Wrench className="w-5 h-5 mr-2 text-[#c4d600]" />}
                            {spec.category === 'Énergie' && <Zap className="w-5 h-5 mr-2 text-[#c4d600]" />}
                            {spec.category === 'Finitions' && <Palette className="w-5 h-5 mr-2 text-[#c4d600]" />}
                            {spec.category === 'Sécurité' && <CheckCircle className="w-5 h-5 mr-2 text-[#c4d600]" />}
                            {spec.category}
                          </h3>
                          <ul className="space-y-2">
                            {spec.items.map((item, itemIndex) => (
                              <li key={itemIndex} className="flex items-center text-gray-700">
                                <CheckCircle className="w-4 h-4 text-[#c4d600] mr-2 flex-shrink-0" />
                                {item}
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  </div>
                </AnimatedSection>
              </>
            )}

            {/* Plans Tab */}
            {activeTab === 'plans' && (
              <AnimatedSection animation="fade-up">
                <div>
                  <h2 className="text-3xl font-bold text-gray-800 mb-6">Plans d'étage</h2>
                  <div className="space-y-8">
                    {details.floorPlans.map((plan, index) => (
                      <div key={index} className="bg-gray-50 rounded-lg p-6">
                        <h3 className="text-2xl font-bold text-gray-800 mb-4">{plan.name}</h3>
                        <div className="rounded-lg overflow-hidden shadow-lg">
                          <img
                            src={plan.image}
                            alt={`Plan ${plan.name}`}
                            className="w-full h-auto"
                            loading="lazy"
                            onError={(e) => {
                              e.target.src = 'https://via.placeholder.com/800x600?text=Plan+non+disponible';
                            }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </AnimatedSection>
            )}

            {/* Personalization Tab */}
            {activeTab === 'personalization' && (
              <AnimatedSection animation="fade-up">
                <div>
                  <h2 className="text-3xl font-bold text-gray-800 mb-6">Options de personnalisation</h2>
                  <p className="text-gray-600 text-lg mb-6">
                    Ce modèle peut être entièrement personnalisé selon vos goûts et besoins. Voici les principales options disponibles :
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {details.personalization.map((option, index) => (
                      <div
                        key={index}
                        className="flex items-start bg-gradient-to-r from-[#c4d600]/10 to-transparent rounded-lg p-4 border border-[#c4d600]/20"
                      >
                        <Lightbulb className="w-5 h-5 text-[#c4d600] mr-3 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{option}</span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-8 bg-[#c4d600]/10 rounded-lg p-6 border border-[#c4d600]/20">
                    <h3 className="text-xl font-bold text-gray-800 mb-3">Personnalisation sur mesure</h3>
                    <p className="text-gray-600 mb-4">
                      Au-delà de ces options standards, nos architectes peuvent concevoir des modifications spécifiques pour adapter ce modèle à votre terrain, votre budget et vos besoins particuliers.
                    </p>
                    <Button
                      asChild
                      className="bg-[#c4d600] hover:bg-[#b0c200] text-gray-800"
                    >
                      <Link to="/contact">
                        Discuter de votre projet
                      </Link>
                    </Button>
                  </div>
                </div>
              </AnimatedSection>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Contact Card */}
            <AnimatedSection animation="slide-left">
              <div className="bg-gray-50 rounded-lg p-6 sticky top-32">
                <h3 className="text-2xl font-bold text-gray-800 mb-4">Intéressé par ce modèle ?</h3>
                <p className="text-gray-600 mb-6">
                  Contactez-nous pour obtenir un devis personnalisé ou pour visiter une maison témoin.
                </p>
                <div className="space-y-3">
                  <Button
                    asChild
                    className="w-full bg-[#c4d600] hover:bg-[#b0c200] text-gray-800 font-semibold"
                  >
                    <a href={`mailto:Contact@artduchalet.com?subject=Demande d'information - ${model.title}`}>
                      <Mail className="w-4 h-4 mr-2" />
                      Demander un devis
                    </a>
                  </Button>
                  <Button
                    asChild
                    variant="outline"
                    className="w-full"
                  >
                    <a href={`tel:0492000000`}>
                      <Phone className="w-4 h-4 mr-2" />
                      Appeler maintenant
                    </a>
                  </Button>
                  <Button
                    asChild
                    variant="outline"
                    className="w-full"
                  >
                    <Link to="/contact">
                      <Calendar className="w-4 h-4 mr-2" />
                      Prendre rendez-vous
                    </Link>
                  </Button>
                </div>
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <p className="text-sm text-gray-600 mb-2">Référence modèle</p>
                  <p className="font-mono text-lg font-bold text-gray-800">{model.name}</p>
                </div>
              </div>
            </AnimatedSection>

            {/* Quick Stats */}
            <AnimatedSection animation="slide-left" delay={0.1}>
              <div className="bg-gradient-to-br from-[#c4d600] to-[#b0c200] rounded-lg p-6 text-white">
                <h3 className="text-xl font-bold mb-4">Résumé</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="opacity-90">Surface</span>
                    <span className="font-bold">{details.characteristics.surface} m²</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="opacity-90">Pièces</span>
                    <span className="font-bold">{details.characteristics.rooms}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="opacity-90">Chambres</span>
                    <span className="font-bold">{details.characteristics.bedrooms}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="opacity-90">Salles de bain</span>
                    <span className="font-bold">{details.characteristics.bathrooms}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="opacity-90">Classe énergie</span>
                    <span className="font-bold">{details.characteristics.energyClass}</span>
                  </div>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </div>

      {/* Similar Models */}
      {similarModels.length > 0 && (
        <AnimatedSection animation="fade-up">
          <div className="bg-gray-50 py-16">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <h2 className="text-3xl font-bold text-gray-800 mb-8">Modèles similaires</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {similarModels.map((similarModel) => (
                  <Link
                    key={similarModel.id}
                    to={`/modele/${similarModel.id}`}
                    className="bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 group"
                  >
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={similarModel.image}
                        alt={similarModel.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        loading="lazy"
                        onError={(e) => {
                          e.target.src = 'https://via.placeholder.com/800x600?text=Image+non+disponible';
                        }}
                      />
                    </div>
                    <div className="p-5">
                      <p className="text-sm text-[#c4d600] font-semibold mb-1">Modèle {similarModel.name}</p>
                      <h3 className="font-bold text-gray-800 mb-2 group-hover:text-[#c4d600] transition-colors">
                        {similarModel.title}
                      </h3>
                      <p className="text-sm text-gray-600">{similarModel.type} • {similarModel.style}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </AnimatedSection>
      )}

      {/* CTA Section */}
      <AnimatedSection animation="fade-up">
        <div className="bg-[#c4d600] py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">
              Prêt à construire votre {model.title} ?
            </h2>
            <p className="text-lg text-gray-700 mb-8">
              Contactez notre équipe pour discuter de votre projet et obtenir un devis personnalisé.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                asChild
                size="lg"
                className="bg-gray-800 hover:bg-gray-900 text-white"
              >
                <Link to="/contact">
                  Demander un devis gratuit
                </Link>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="border-gray-800 text-gray-800 hover:bg-gray-800 hover:text-white"
              >
                <Link to="/modeles">
                  Voir tous les modèles
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </AnimatedSection>
    </div>
  );
};

export default ModelDetailPage;

